import axios from 'axios'
import { firebaseAuth } from '../helpers/firebase'
import { API_ERROR_CODE_MAP, HTTP_STATUS_MESSAGE_MAP } from '../constants/userFacingErrors'

const baseUrl = process.env.REACT_APP_API_URL
const codes = {
  UNAUTHORIZED: [401],
  CUSTOM_TOKEN_EXPIRED: -2,
  REQUEST_TIMEOUT: 408,
  ECONNABORTED: 'ECONNABORTED',
}

const getNewToken = async () => {
  const token = await firebaseAuth?.currentUser?.getIdToken()
  return token
}
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (codes.UNAUTHORIZED.includes(error?.response?.status)) {
      firebaseAuth.signOut()
    }

    if (
      error?.response?.status === codes.REQUEST_TIMEOUT ||
      error.code === codes.ECONNABORTED
    ) {
      //Looks like the server is taking to long to respond, please try again in sometime.
      console.log(`A timeout happend on url ${error.config.url}`)
      //errorToast({ content: 'Server request timed out. Please retry again.' })
    }

    return Promise.reject(error)
  }
)

export const getFullUrl = (url) => {
  return `${baseUrl}${url}`
}

/**
 * @typedef {{ subUrl: string;  method?: Method;  data?: object;  params?: object;  headers?: object; cancelToken?:any}} Irequest
 */

/**
 * @param {Irequest} request
 */
export const get = (request) => {
  return commonFetch({ method: 'get', ...request })
}

/**
 * @param {Irequest} request
 */
export const post = (request) => {
  return commonFetch({ method: 'post', ...request })
}

/**
 * @param {Irequest} request
 */
export const patch = (request) => {
  return commonFetch({ method: 'patch', ...request })
}

/**
 * @param {Irequest} request
 */
export const put = (request) => {
  return commonFetch({ method: 'put', ...request })
}

/**
 * @param {Irequest} request
 */
export const deletee = (request) => {
  return commonFetch({ method: 'delete', ...request })
}
/**
 * @param {Irequest} request
 */
const commonFetch = async (request) => {
  const {
    subUrl,
    method,
    url,
    data = {},
    params,
    headers = {},
    cancelToken,
    ...otherProps
  } = request
  const commonHeaders = await getCommonHeaders()
  return axios({
    method,
    url: url || getFullUrl(subUrl),
    params,
    data,
    headers: { ...commonHeaders, ...headers },
    cancelToken,
    ...otherProps
  })
}

export const content_types = {
  multipart: {
    'Content-Type': 'multipart/form-data',
  },
  json: {
    'Content-Type': 'application/json',
  },
}

const getCommonHeaders = async () => {
  // const { auth } = store.getState()
  const token = await getNewToken()
  return {
    ...content_types.json,
    Authorization: token ? `Bearer ${token}` : null,
    token_type: 'id',
    tenant: 'north-metro-100bm'
  }
}

export const convertToForm = (item) => {
  const form_data = new FormData()
  for (const key in item) {
    const value = item[key]
    if (value) {
      form_data.append(key, value)
    }
  }
  return form_data
}

const MAX_DISPLAY_ERROR_LENGTH = 500

/**
 * Map known backend error codes to user-friendly messages (from constants/userFacingErrors).
 * When backend sends { errorCode: "..." } or { code: "..." }, we show this instead of technical text.
 */
export const API_ERROR_MESSAGES = API_ERROR_CODE_MAP

/** Safe string from backend for display; avoids stack traces or huge payloads breaking UI. */
const sanitizeDisplayMessage = (str) => {
  if (typeof str !== 'string') return ''
  const trimmed = str.trim()
  if (!trimmed) return ''
  if (trimmed.length > MAX_DISPLAY_ERROR_LENGTH) return trimmed.slice(0, MAX_DISPLAY_ERROR_LENGTH) + '…'
  return trimmed
}

/**
 * Get error message from API error response.
 * Applies to every API call; no per-endpoint logic needed.
 * 1) HTTP status (401, 403, 404, 408, 429, 5xx) → mapped message from HTTP_STATUS_MESSAGE_MAP.
 * 2) Backend body errorCode/code → mapped message from API_ERROR_MESSAGES.
 * 3) Backend body error or message → sanitized string.
 * Never throws; returns empty string when no message or on unexpected shape.
 * @param {Error} error - Axios or API error
 * @returns {string} Backend message or empty string
 */
export const getErrorMessage = (error) => {
  try {
    const status = error?.response?.status
    if (typeof status === 'number' && HTTP_STATUS_MESSAGE_MAP[status]) {
      return HTTP_STATUS_MESSAGE_MAP[status]
    }
    const data = error?.response?.data
    if (!data || typeof data !== 'object') return ''
    const code = data.errorCode || data.code
    if (typeof code === 'string' && API_ERROR_MESSAGES[code]) return API_ERROR_MESSAGES[code]
    if (typeof data.error === 'string') return sanitizeDisplayMessage(data.error)
    if (typeof data.message === 'string') return sanitizeDisplayMessage(data.message)
    return ''
  } catch (_) {
    return ''
  }
}

/**
 * Get message to show in toast: backend message if present, otherwise fallback.
 * Use this so backend and frontend show the same error text.
 * Never throws; always returns a string (fallback if needed).
 * @param {Error} error - Axios or API error
 * @param {string} fallback - Message when backend sends no error/message
 * @returns {Promise<string>} Resolves with message to display (async for blob parsing)
 */
export const getApiErrorMessage = async (error, fallback = 'An error occurred. Please try again.') => {
  try {
    const status = error?.response?.status
    if (typeof status === 'number' && HTTP_STATUS_MESSAGE_MAP[status]) {
      return HTTP_STATUS_MESSAGE_MAP[status]
    }
    const msg = getErrorMessage(error)
    if (msg) return msg
    const data = error?.response?.data
    if (!data) return typeof fallback === 'string' ? fallback : 'An error occurred. Please try again.'
    if (typeof data.error === 'string' && data.error) return sanitizeDisplayMessage(data.error)
    if (typeof data.message === 'string' && data.message) return sanitizeDisplayMessage(data.message)
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      try {
        const text = await data.text()
        const json = JSON.parse(text)
        if (typeof json.message === 'string' && json.message) return sanitizeDisplayMessage(json.message)
        if (typeof json.error === 'string' && json.error) return sanitizeDisplayMessage(json.error)
      } catch (_) { /* ignore parse errors */ }
    }
    return typeof fallback === 'string' ? fallback : 'An error occurred. Please try again.'
  } catch (_) {
    return typeof fallback === 'string' ? fallback : 'An error occurred. Please try again.'
  }
}

/**
 * Sync version when blob is not used; same as getApiErrorMessage but no blob handling.
 * Never throws; always returns a string.
 */
export const getApiErrorMessageSync = (error, fallback = 'An error occurred. Please try again.') => {
  try {
    const msg = getErrorMessage(error)
    return msg || (typeof fallback === 'string' ? fallback : 'An error occurred. Please try again.')
  } catch (_) {
    return typeof fallback === 'string' ? fallback : 'An error occurred. Please try again.'
  }
}

export const cancellableApiRequest = () => {
  let cancelToken = null
  const apiCall = async (fn, config) => {
    if (cancelToken) {
      cancelToken.cancel()
    }
    cancelToken = axios.CancelToken.source()
    const data = await fn({ ...config, cancelToken: cancelToken.token })
    cancelToken = null
    return data
  }
  return apiCall
}